import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Calendar, Check, ChevronRight, Copy, Lock, Mail, MessageCircle, Zap } from 'lucide-react'
import { ReactNode, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { api, IntegrationItem, MeData, TelegramConnectionInfo } from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'

const BOT_URL = 'https://t.me/DeloAppBot'

interface ServiceDef {
  type: IntegrationItem['type']
  label: string
  icon: ReactNode
  description: string
}

const SERVICES: ServiceDef[] = [
  {
    type: 'gmail',
    label: 'Gmail',
    icon: <Mail size={18} />,
    description: 'Читать и отправлять письма через Delo',
  },
  {
    type: 'google_calendar',
    label: 'Google Календарь',
    icon: <Calendar size={18} />,
    description: 'Просматривать и создавать события',
  },
  {
    type: 'yandex_mail',
    label: 'Яндекс Почта',
    icon: <Mail size={18} />,
    description: 'Читать и отправлять письма через IMAP',
  },
  {
    type: 'apple_calendar',
    label: 'Apple Calendar',
    icon: <Calendar size={18} />,
    description: 'Просматривать и создавать события iCloud',
  },
]

export function Integrations() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { haptic, confirm } = useTelegram()

  const { data, isLoading, error, refetch } = useQuery<IntegrationItem[]>({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/integrations'),
    staleTime: 60_000,
  })

  const { data: me } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
  })
  // Почта/Календарь продаются с тарифа Plus и выше
  const isPaid = (me?.subscription.tier ?? 'free') !== 'free'

  const { data: tgConn } = useQuery<TelegramConnectionInfo | null>({
    queryKey: ['telegram-connection'],
    queryFn: () => api.get<TelegramConnectionInfo | null>('/api/telegram/connection').catch(() => null),
    staleTime: 60_000,
  })

  const revokeMutation = useMutation({
    mutationFn: (type: string) => api.delete(`/api/integrations/${type}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] })
      qc.invalidateQueries({ queryKey: ['today'] })
    },
    onError: () => {
      WebApp.showAlert('Не удалось отключить интеграцию. Попробуй ещё раз.')
    },
  })

  // Подключение происходит в чате бота: пользователь сворачивает мини-апп,
  // подключает интеграцию и возвращается. При возврате обновляем статусы, чтобы
  // свежеподключённая интеграция сразу отобразилась (staleTime не мешал бы).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      qc.invalidateQueries({ queryKey: ['integrations'] })
      qc.invalidateQueries({ queryKey: ['telegram-connection'] })
      qc.invalidateQueries({ queryKey: ['auto-reply'] })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [qc])

  const connectedMap = new Map(
    (data ?? []).map(i => [i.type, i])
  )

  const handleConnect = (type: string) => {
    haptic.impactOccurred('light')
    // Не платный тариф — ведём на апгрейд (почта/календарь доступны с Plus)
    if (!isPaid) {
      navigate('/subscription')
      return
    }
    // Платный — открываем бота сразу на нужном сервисе
    WebApp.openTelegramLink(`${BOT_URL}?start=connect_${type}`)
  }

  const handleChangeGmailScope = () => {
    haptic.impactOccurred('light')
    WebApp.openTelegramLink(`${BOT_URL}?start=connect_gmail`)
  }

  const handleRevoke = (type: string, label: string) => {
    haptic.impactOccurred('light')
    confirm(`Отключить ${label}?`, ok => {
      if (ok) revokeMutation.mutate(type)
    })
  }

  const handleConnectTelegram = () => {
    haptic.impactOccurred('light')
    WebApp.openTelegramLink(`${BOT_URL}?start=connect_telegram`)
  }

  const handleImportTelegram = () => {
    haptic.impactOccurred('light')
    WebApp.openTelegramLink(`${BOT_URL}?start=import_telegram`)
  }

  const handleCopyEmail = (email: string) => {
    haptic.impactOccurred('light')
    navigator.clipboard.writeText(email).catch(() => {})
    WebApp.showAlert(`Скопировано: ${email}`)
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Интеграции</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-4">
        {isLoading && Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)}
        {error && <ErrorCard onRetry={refetch} />}

        {!isLoading && (
          <>
            {/* Telegram Chats card */}
            <div className="bg-surface-1 border border-line-1 rounded-lg overflow-hidden">
              <div className="p-4 flex items-center gap-3">
                <div className="relative shrink-0">
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                    tgConn?.is_enabled ? 'bg-accent-dim text-accent' : 'bg-surface-2 text-ink-2'
                  }`}>
                    <MessageCircle size={18} />
                  </div>
                  {tgConn?.is_enabled && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success border-2 border-surface-1" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-ink">Telegram-чаты</p>
                  {tgConn?.is_enabled ? (
                    <p className="text-sm text-ink-2">Подключено · наблюдаю за чатами</p>
                  ) : (
                    <p className="text-sm text-ink-2">Мониторинг договорённостей и контактов</p>
                  )}
                </div>
              </div>
              <div className="border-t border-line-1 px-4 py-3 flex gap-2">
                <button
                  onClick={handleConnectTelegram}
                  className="btn-active flex-1 text-xs font-medium text-accent bg-accent-dim px-2.5 py-1.5 rounded-md text-center"
                >
                  {tgConn?.is_enabled ? 'Настроить' : 'Подключить'}
                </button>
                <button
                  onClick={handleImportTelegram}
                  className="btn-active flex-1 text-xs font-medium text-ink-1 bg-surface-2 px-2.5 py-1.5 rounded-md text-center"
                >
                  Загрузить архив
                </button>
              </div>
              <p className="px-4 pb-3 text-xs text-ink-3">
                Требуется Telegram Premium и тариф Pro
              </p>

              {/* Настройки чатов переехали на свой экран: автоответчик,
                  расшифровка голосовых и слежение за удалёнными были спрятаны
                  здесь, за скроллом, там где человек ищет «подключить почту».
                  Отсюда — только дверь туда. */}
              {tgConn?.is_enabled && (
                <div className="border-t border-line-1">
                  <Link
                    to="/settings/telegram"
                    className="btn-active w-full px-4 py-3 flex items-center gap-2 text-left"
                  >
                    <span className="flex-1 text-sm text-ink">Настройки чатов</span>
                    <ChevronRight size={16} className="text-ink-2" />
                  </Link>
                </div>
              )}
            </div>

            {/* Standard integrations */}
            <div className="bg-surface-1 border border-line-1 rounded-lg divide-y divide-line">
              {SERVICES.map(({ type, label, icon, description }) => {
                const connected = connectedMap.get(type as IntegrationItem['type'])
                return (
                  <div key={type} className="p-4 flex items-center gap-3">
                    {/* Icon with status dot */}
                    <div className="relative shrink-0">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                        connected ? 'bg-accent-dim text-accent' : 'bg-surface-2 text-ink-2'
                      }`}>
                        {icon}
                      </div>
                      {connected && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success border-2 border-surface-1" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-ink">{label}</p>
                      {connected?.email ? (
                        <button
                          onClick={() => handleCopyEmail(connected.email!)}
                          className="btn-active flex items-center gap-1 text-sm text-ink-2 truncate max-w-full"
                          title="Нажми чтобы скопировать"
                        >
                          <span className="truncate">{connected.email}</span>
                          <Copy size={11} className="shrink-0 text-ink-3" />
                        </button>
                      ) : (
                        <p className="text-sm text-ink-2">{description}</p>
                      )}
                      {/* Scope badge для Gmail */}
                      {connected && type === 'gmail' && (
                        <p className="text-xs text-ink-3 mt-0.5">
                          {connected.gmail_scope === 'readonly' ? 'Только чтение' : 'Чтение и отправка'}
                        </p>
                      )}
                    </div>

                    {connected ? (
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        {type === 'gmail' && (
                          <button
                            onClick={handleChangeGmailScope}
                            className="btn-active text-xs font-medium text-accent bg-accent-dim px-2.5 py-1.5 rounded-md"
                          >
                            Изменить доступ
                          </button>
                        )}
                        <button
                          onClick={() => handleRevoke(type, label)}
                          disabled={revokeMutation.isPending}
                          className="btn-active text-xs font-medium text-danger bg-danger-dim px-2.5 py-1.5 rounded-md disabled:opacity-40"
                        >
                          Отключить
                        </button>
                      </div>
                    ) : isPaid ? (
                      <button
                        onClick={() => handleConnect(type)}
                        className="btn-active text-xs font-medium text-accent bg-accent-dim px-2.5 py-1.5 rounded-md"
                      >
                        Подключить
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnect(type)}
                        className="btn-active flex items-center gap-1 text-xs font-medium text-ink-2 bg-surface-2 px-2.5 py-1.5 rounded-md"
                        title="Доступно на тарифе Plus и выше"
                      >
                        <Lock size={11} className="shrink-0" />
                        Plus
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="bg-surface-1 border border-line-1 rounded-lg p-4 flex items-start gap-3">
              <Zap size={15} className="text-ink-2 shrink-0 mt-0.5" />
              <p className="text-sm text-ink-1">
                Почта, Календарь, Яндекс Почта и Apple Calendar доступны на платных тарифах (Plus и Pro). Подключение происходит через бота — напиши /connect.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
